package app.footyguide.web.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.domain.model.Game
import app.footyguide.domain.model.Team
import app.footyguide.web.data.WebChannelRepository
import app.footyguide.web.data.WebCompetitionRepository
import app.footyguide.web.page.displayName
import app.footyguide.web.page.extractKickoffTime
import app.footyguide.web.page.resolveTeam
import app.footyguide.web.page.teamMatchesId

fun formatBannerDate(
    date: String,
    strings: app.footyguide.core.locale.AppStrings? = null,
): String {
    val parts = date.split("-")
    if (parts.size != 3) return date
    val monthIdx = parts[1].toIntOrNull()?.minus(1) ?: -1
    val months = strings?.monthsShort
    val mes = if (months != null && monthIdx in months.indices) {
        months[monthIdx]
    } else {
        when (parts[1]) {
            "01" -> "jan"
            "02" -> "fev"
            "03" -> "mar"
            "04" -> "abr"
            "05" -> "mai"
            "06" -> "jun"
            "07" -> "jul"
            "08" -> "ago"
            "09" -> "set"
            "10" -> "out"
            "11" -> "nov"
            "12" -> "dez"
            else -> parts[1]
        }
    }
    return "${parts[2]} $mes"
}

fun buildBannerMatchup(teamInitials: String, opponentInitials: String, isHome: Boolean): String {
    return if (isHome) {
        "$teamInitials × $opponentInitials"
    } else {
        "$opponentInitials × $teamInitials"
    }
}

/**
 * Stitch Design System (Screen 3: Home Tematizada / ThemedClubHero).
 * Editorial Broad-Sheet hero card with club primary color accent,
 * prominent matchup, date/time and visual broadcaster channel badges.
 */
@Composable
fun NextGameBanner(
    game: Game,
    team: Team?,
    teams: Map<String, Team>,
    competitions: Map<String, WebCompetitionRepository.CompetitionData>,
    channels: Map<String, WebChannelRepository.ChannelData>,
    onClick: () -> Unit
) {
    val strings = app.footyguide.core.locale.LocalStrings.current
    val isHome = teamMatchesId(team, game.homeTeamId)
    val opponentId = if (isHome) game.awayTeamId else game.homeTeamId
    val opponent = resolveTeam(teams, opponentId)
    val competition = competitions[game.competitionId]
    val opponentLogo = opponent?.logo
    val opponentName = opponent?.name ?: displayName(opponentId)
    val competitionName = competition?.shortName?.takeIf { it.isNotBlank() }
        ?: competition?.name
        ?: game.competitionId

    val kickoffTime = extractKickoffTime(game.kickoff)
    val dateLabel = formatBannerDate(game.date, strings)
    val catalogClub = app.footyguide.core.theme.ClubCatalog.bySlug(team?.id ?: "")
    val rawPrimary = catalogClub?.c1 ?: team?.primaryColor?.takeIf { it.uppercase() != "#FFFFFF" } ?: "#0B6E4F"
    val rawSecondary = catalogClub?.c2 ?: team?.secondaryColor
    val derivedColors = app.footyguide.core.theme.ClubColorEngine.derive(rawPrimary, rawSecondary)
    val teamColor = derivedColors.fill
    val myInitials = catalogClub?.initials ?: resolveClubInitials(team?.id ?: "")
    val oppInitials = app.footyguide.core.theme.ClubCatalog.bySlug(opponentId)?.initials ?: resolveClubInitials(opponentId)

    Div({
        onClick { onClick() }
        style {
            margin(12.px, 16.px, 20.px)
            backgroundColor(Color("#FFFFFF"))
            border(1.px, LineStyle.Solid, Color("#E7E7E1"))
            property("border-left", "4px solid $teamColor")
            borderRadius(16.px)
            padding(16.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            cursor("pointer")
            property("box-shadow", "0 2px 8px rgba(22, 24, 29, 0.04)")
            property("transition", "all 0.2s ease")
        }
    }) {
        // TOP ROW: CONTEXT & COMPETITION
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                }
            }) {
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(800)
                        fontFamily("Sora, 'Plus Jakarta Sans', sans-serif")
                        color(Color(teamColor))
                        property("text-transform", "uppercase")
                        property("white-space", "nowrap")
                        flexShrink(0)
                        letterSpacing(0.6.px)
                    }
                }) {
                    Text(strings.nextGame.uppercase())
                }
                Span({
                    style {
                        fontSize(11.px)
                        color(Color("#A1A1AA"))
                        fontWeight(600)
                    }
                }) {
                    Text("•")
                }
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(700)
                        fontFamily("Sora, 'Plus Jakarta Sans', sans-serif")
                        color(Color("#71717A"))
                        property("text-transform", "uppercase")
                    }
                }) {
                    Text(competitionName)
                }
            }

            Span({
                style {
                    fontSize(12.px)
                    fontWeight(700)
                    fontFamily("Sora, sans-serif")
                    color(Color("#16181D"))
                }
            }) {
                Text("$dateLabel · $kickoffTime")
            }
        }

        // MATCHUP ROW: CRESTS + NAMES
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                // Home crest
                val homeLogo = if (isHome) team?.logo else opponentLogo
                SafeImg(
                    src = homeLogo,
                    fallbackIcon = Icons.SHIELD,
                    fallbackSize = CrestSizes.HERO_CARD_FALLBACK,
                    modifier = {
                        width(CrestSizes.HERO_CARD_PX.px)
                        height(CrestSizes.HERO_CARD_PX.px)
                        property("width", CrestSizes.HERO_CARD_CSS)
                        property("height", CrestSizes.HERO_CARD_CSS)
                        property("object-fit", "contain")
                    }
                )

                Span({
                    style {
                        fontSize(18.px)
                        fontWeight(800)
                        fontFamily("Sora, sans-serif")
                        color(Color("#16181D"))
                        letterSpacing((-0.3).px)
                    }
                }) {
                    Text(buildBannerMatchup(myInitials, oppInitials, isHome))
                }

                // Away crest
                val awayLogo = if (isHome) opponentLogo else team?.logo
                SafeImg(
                    src = awayLogo,
                    fallbackIcon = Icons.SHIELD,
                    fallbackSize = CrestSizes.HERO_CARD_FALLBACK,
                    modifier = {
                        width(CrestSizes.HERO_CARD_PX.px)
                        height(CrestSizes.HERO_CARD_PX.px)
                        property("width", CrestSizes.HERO_CARD_CSS)
                        property("height", CrestSizes.HERO_CARD_CSS)
                        property("object-fit", "contain")
                    }
                )
            }

            Span({
                style {
                    color(Color(teamColor))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                }
            }) {
                LucideIcon(Icons.CHEVRON_RIGHT, size = 20, color = teamColor)
            }
        }

        // BOTTOM ROW: BROADCAST CHANNELS BADGES
        val gameChannels = game.channelsFor("BR")
        if (gameChannels.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    paddingTop(8.px)
                    property("border-top", "1px solid #F4F4F5")
                }
            }) {
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(700)
                        fontFamily("Sora, 'Plus Jakarta Sans', sans-serif")
                        color(Color("#71717A"))
                        property("text-transform", "uppercase")
                        letterSpacing(0.4.px)
                    }
                }) {
                    Text("${strings.whereToWatch.replace("📺", "").trim().uppercase()}:")
                }
                ChannelBadgesRow(gameChannels)
            }
        }
    }
}
